// Data Contracts
import {
  JudgeReviewDocumentAction,
  JudgeReviewFilingConfiguration,
} from '@data-contracts';

/**
 * Pure config-navigation helpers for the judge-review filing configuration.
 *
 * The mappers ({@link mapDispositionLabel}, {@link mapConfirmOutcome}) and the
 * data service both resolve a document's judicial action through the same chain:
 * find the per-document action config by document id, then the matching
 * user-setting. Centralising it here means a contract-shape change (or the
 * `?? -1` sentinel) is fixed in one place instead of drifting across four copies.
 */

/**
 * Finds the per-document action configuration for a document id. Applies the
 * `?? -1` sentinel so a null document id never matches a real config row.
 */
export function findDocumentActionConfig(
  config: JudgeReviewFilingConfiguration | null | undefined,
  documentId: number | null | undefined,
): JudgeReviewDocumentAction | undefined {
  return config?.judgeReviewDocumentActions?.find(
    action => action.documentId === (documentId ?? -1),
  );
}

/**
 * Resolves the display name of a document's selected judicial action, or null
 * when the document / action is not present in the configuration.
 */
export function resolveJudicialActionName(
  config: JudgeReviewFilingConfiguration | null | undefined,
  documentId: number | null | undefined,
  judicialActionId: string | null | undefined,
): string | null {
  return (
    findDocumentActionConfig(config, documentId)?.judgeReviewUserSettings?.find(
      setting => setting.judicialActionId === judicialActionId,
    )?.judicialAction ?? null
  );
}

/**
 * Whether a document's selected judicial action creates a new filing (grant-type,
 * Review & Sign studio) rather than a record-only status update. Defaults to true
 * when the flag is unknown, mirroring the previous inline `createNewFiling !== false`
 * check.
 */
export function resolveCreatesNewFiling(
  config: JudgeReviewFilingConfiguration | null | undefined,
  documentId: number | null | undefined,
  judicialActionId: string | null | undefined,
): boolean {
  const setting = findDocumentActionConfig(
    config,
    documentId,
  )?.judgeReviewUserSettings?.find(
    candidate => candidate.judicialActionId === judicialActionId,
  );
  return setting?.createNewFiling !== false;
}
